import {initAvailabilityPopovers} from "../Functions/Popovers";
import {Popover} from "bootstrap";
import DropdownHelper from "../Modules/Dropdown";

/**
 * Infinite scroll options
 */
interface InfiniteScrollOptions {
    itemsPerPage: number;
    maxPage: number;
    page: number;
    url: string;
    target: string;
    buttonMode: boolean;
    disableLoading: boolean;
    data: string;
    method: string;
}

/**
 * Get loading element or create a new template
 *
 * @param wrapper
 */
function getLoadingElementOrNewTemplate (wrapper: HTMLElement) {
    let loadingElement = wrapper.querySelector('.infinite-scroll-loading');

    if (loadingElement) {
        return loadingElement as HTMLElement;
    }

    loadingElement = document.createElement('div');
    loadingElement.classList.add('infinite-scroll-loading', 'd-none');
    loadingElement.innerHTML = '<div class="text-center fa-3x"><i class="fas fa-spinner animated-spin"></i></div>';

    if (wrapper.tagName === 'TABLE') {
        const tr = document.createElement('tr');
        const td = document.createElement('td');
        td.setAttribute('colspan', '100%');
        tr.classList.add('infinite-scroll-loader');

        const newNode = td.appendChild(loadingElement);
        tr.appendChild(td);

        wrapper.appendChild(tr);

        return newNode as HTMLElement;
    }

    if (wrapper.tagName === 'UL') {
        const li = document.createElement('li');
        li.classList.add('infinite-scroll-loader');
        const newNode = li.appendChild(loadingElement);

        wrapper.appendChild(li);

        return newNode as HTMLElement;
    }

    const newNode = wrapper.appendChild(loadingElement);

    return newNode as HTMLElement;
}

/**
 * Infinite scroll
 *
 * @param element
 * @param options
 */
export function infiniteScroll(element: HTMLElement, options: InfiniteScrollOptions = null) {
    let data = [];

    for (let [key, value] of Object.entries(element.dataset)) {
        if (!key || !value) {
            continue;
        }

        let newKey = key.replace(/infiniteScroll/, '');

        if (newKey !== key) {
            newKey = newKey.charAt(0).toLowerCase() + newKey.slice(1);

            if (
                newKey === 'itemsPerPage'
                || newKey === 'page'
                || newKey === 'maxPage'
            ) {
                value = parseInt(value);
            }
            else if (
                newKey === 'buttonMode'
                || newKey === 'disableLoading'
            ) {
                value = (value === 'true');
            }

            data[newKey] = value;
        }
    }

    const opts = Object.assign({}, data, options);
    const target = document.querySelector(opts.target) as HTMLElement;
    let triggered = false;
    let page = opts.page;
    let itemsPerPage = opts.itemsPerPage;
    let maxPage = opts.maxPage;
    let latestScrollY = window.scrollY;

    let loadingElement = getLoadingElementOrNewTemplate(element);

    /**
     * Load more items on a button click
     * @param e
     */
    const buttonEventFunction = (e: Event) => {
        const target = (e.target) as HTMLButtonElement;
        target.disabled = true;

        if (triggered) {
            return;
        }

        triggered = true;
        if (page <= maxPage) {
            loadMoreItems();
        }
    }

    /**
     * Load more items on scroll
     */
    const scrollEventFunction = () => {
        if (!loadingElement) {
            return;
        }

        if (getBottom() <= window.innerHeight && getBottom() > 0 && !triggered && window.scrollY > latestScrollY) {
            triggered = true;

            if (page > maxPage) {
                loadingElement?.classList.add('d-none');
                window.removeEventListener('scroll', scrollEventFunction);
            } else {
                loadMoreItems();
            }
        }

        latestScrollY = window.scrollY;
    }

    /**
     * Show loading spinner
     */
    const showLoading = () => {
        loadingElement?.classList.add('loading');
        loadingElement?.classList.remove('d-none');
    }

    /**
     * Hide loading spinner
     */
    const hideLoading = () => {
        loadingElement?.classList.remove('loading');
        loadingElement?.classList.add('d-none');
    }

    const flattenObject = (obj: Object, parentKey: String = '') => {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            const newKey = parentKey ? `${parentKey}[${key}]` : key;
            if (typeof value === 'object' && value !== null) {
                return {...acc, ...flattenObject(value, newKey)};
            }
            if (Array.isArray(value)) {
                return value.reduce(
                    (arrAcc, item, index) => ({
                        ...arrAcc,
                        [`${newKey}[${index}]`]: item,
                    }),
                    acc
                );
            }
            if(!!value) {
                return {...acc, [newKey]: value};
            } else {
                return acc;
            }
        }, {});
    }

    /**
     * Load more items
     */
    const loadMoreItems = () => {
        showLoading();

        const request = async () => {
            const url = new URL(opts.url, window.location.href);

            const data = opts.data ? JSON.parse(opts.data) : {};

            const query = {
                itemsPerPage: String(itemsPerPage),
                page: String(page),
                buttonMode: opts.buttonMode,
                ...data
            };

            if (opts.method?.toUpperCase() === 'GET' || opts.method === undefined) {
                url.search = (url.search ? url.search + '&' : '') + new URLSearchParams(flattenObject(query)).toString();
            }

            const fetchOptions: RequestInit = {
                method: opts.method,
                mode: 'cors',
                cache: 'no-cache',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
                redirect: 'follow',
                referrerPolicy: 'no-referrer',
                body: undefined
            }

            if (opts.method?.toUpperCase() === 'POST') {
                fetchOptions.body = JSON.stringify(query);
            }

            const response = await fetch(
                url.toString(),
                fetchOptions
            )

            return {
                html: await response.text(),
                status: response.status
            };
        }

        request()
            .then((data) => {
                hideLoading();

                if (data.status == 200) {
                    element.querySelector('.infinite-scroll-loading')?.remove();
                    element.querySelector('.infinite-scroll-load-more')?.remove();

                    if (data.html == '') {
                        window.removeEventListener('scroll', scrollEventFunction);
                        return;
                    }

                    target.insertAdjacentHTML('beforeend', data.html);

                    if (page > maxPage) {
                        element.querySelector('.infinite-scroll-load-more')?.remove();
                    }
                }

                initAvailabilityPopovers();

                // @ts-ignore
                $(element).find('*[data-annotations]').annotations();
                // @ts-ignore
                $(element).find('.price-async').prices();
                // @ts-ignore
                $(element).find('.shopping-button-async').shoppingButton();
                // @ts-ignore
                $(element).find('*[data-fetch-article-buy]').articleBuy();

                const popovers = element.querySelectorAll('[data-bs-toggle="popover"]');
                if (popovers.length) {
                    popovers.forEach((popoverEl) => {
                        const popover = Popover.getOrCreateInstance(popoverEl, {
                            container: 'body'
                        });
                    });
                }

                if (element.querySelectorAll('.equalize-height').length > 0) {
                    // @ts-ignore
                    $(element).find('.equalize-height').equalHeights('reset').equalHeights();
                }

                // @ts-ignore
                window.Waypoint.refreshAll();

                loadingElement = getLoadingElementOrNewTemplate(element);
                page++;
                triggered = false;
            })
            .then(() => {
                DropdownHelper.defaultInit();
            })
        .catch((error) => {
            hideLoading();
            console.error(error);
        });
    }

    /**
     * Get bottom position of the element
     */
    const getBottom = () => {
        return element.getBoundingClientRect().top + element.clientHeight;
    }

    // Init
    if (!opts?.disableLoading) {
        if (!opts?.buttonMode) {
            window.addEventListener('scroll', scrollEventFunction);
        } else {
            element.querySelector('.infinite-scroll-load-more')?.addEventListener('click', buttonEventFunction);
        }
    }
}

document.querySelectorAll('.infinite-scroll').forEach((element) => {
    infiniteScroll(element as HTMLElement);
});
